import { useState } from 'react'
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useHover,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
    safePolygon,
} from '@floating-ui/react'
import { CaretRightIcon } from '@phosphor-icons/react'
import styles from './ScoopMenu.module.scss'

/**
 * ScoopMenuItemWithSubmenu - Menu item that displays a submenu on hover
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} [props.leftSection] - Content on the left (typically an icon)
 * @param {string} [props.primary] - Primary text (bold, main content)
 * @param {string} [props.secondary] - Secondary text (muted, description)
 * @param {React.ReactNode} props.submenu - Submenu content to display
 * @param {string} [props.placement='right-start'] - Submenu placement
 * @param {number} [props.offset=8] - Distance between item and submenu
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Inline styles
 */
export const ScoopMenuItemWithSubmenu = ({
    leftSection,
    primary,
    secondary,
    submenu,
    placement = 'right-start',
    offset: offsetValue = 8,
    className = '',
    style,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false)

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement,
        middleware: [offset(offsetValue), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    })

    // Use safePolygon to allow mouse movement in a "safe" area between trigger and submenu
    const hover = useHover(context, {
        move: false,
        handleClose: safePolygon({
            buffer: 1, // Small buffer area to make it easier to move to submenu
        }),
        delay: {
            open: 75, // Small delay before opening
            close: 150, // Delay before closing to allow mouse movement
        },
    })
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'menu' })

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role])

    const itemClassName = [styles.menuItem, className].filter(Boolean).join(' ')

    return (
        <>
            <div
                ref={refs.setReference}
                className={itemClassName}
                role="menuitem"
                tabIndex={0}
                {...getReferenceProps(props)}
                style={style}
            >
                {leftSection && <span className={styles.leftSection}>{leftSection}</span>}

                {primary || secondary ? (
                    <div className={styles.content}>
                        {primary && <span className={styles.primary}>{primary}</span>}
                        {secondary && <span className={styles.secondary}>{secondary}</span>}
                    </div>
                ) : null}

                <span className={styles.rightSection}>
                    <CaretRightIcon size={16} weight="regular" />
                </span>
            </div>

            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        className={styles.floatingMenuWrapper}
                        style={{
                            ...floatingStyles,
                            opacity: 1,
                            transition: 'opacity 0.15s ease-out',
                        }}
                        {...getFloatingProps()}
                    >
                        <div className={styles.menu}>{submenu}</div>
                    </div>
                </FloatingPortal>
            )}
        </>
    )
}

ScoopMenuItemWithSubmenu.displayName = 'ScoopMenuItemWithSubmenu'
